import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ConversationResetMarker } from '@/lib/conversation-reset-markers';

/** Exported so callers (ResetConversationDialog) can invalidate exactly this query after a
 * reset/undo succeeds, without hardcoding the key string in two places. */
export const CONVERSATION_RESET_MARKERS_QUERY_KEY = 'conversation-reset-markers';

interface ResetMarkerRow {
  id: string;
  reset_at: string;
}

/**
 * Fetches the "Conversation reset" markers for a single conversation, for MessageList to place in
 * the chat thread (src/lib/conversation-reset-markers.ts does the placement).
 *
 * Reads public.get_tenant_conversation_reset_markers
 * (supabase/migrations/20260814140000_add_conversation_reset_markers_rpc.sql), which sources
 * exclusively from <schema>.conversation_events — never <schema>.messages, and never the WhatsApp
 * side of the platform.
 *
 * `get_tenant_conversation_reset_markers` isn't in the generated Database type yet (that type
 * mirrors live prod; this migration hasn't been applied there) — same untyped-RPC-call shape
 * already used in this codebase for the identical reason (src/modules/app-studio/lib/api.ts,
 * src/lib/run-observability/client.ts, and reset_tenant_conversation itself in
 * ResetConversationDialog.tsx).
 */
export function useConversationResetMarkers(
  tenantSchema: string | null | undefined,
  conversationId: string | null | undefined,
) {
  return useQuery({
    queryKey: [CONVERSATION_RESET_MARKERS_QUERY_KEY, tenantSchema, conversationId],
    queryFn: async ({ signal }): Promise<ConversationResetMarker[]> => {
      const { data, error } = await (supabase as any)
        .rpc('get_tenant_conversation_reset_markers', {
          tenant_schema: tenantSchema,
          conversation_id: conversationId,
        })
        .abortSignal(signal);
      if (error) throw error;
      return ((data ?? []) as ResetMarkerRow[]).map((row) => ({ id: row.id, resetAt: row.reset_at }));
    },
    enabled: !!tenantSchema && !!conversationId,
    // Reset is a rare, deliberate operator action — this doesn't need message-hook-grade polling,
    // but it does need to notice a reset made from another device/tab, so a light interval backs
    // up the explicit invalidation ResetConversationDialog does on its own success.
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}
